/**
 * Live match: exact same string, or the stored name starts with what the user has typed so far.
 * No case folding, no trimming of comparison values (only empty checks).
 */
export function draftNameMatchesStoredName(draft, stored) {
  const d = draft == null ? "" : String(draft);
  const s = stored == null ? "" : String(stored);
  if (d.trim() === "" || s === "") return false;
  return s === d || s.startsWith(d);
}

/**
 * All movement name strings from one exercise (saved workout or form values — same shape).
 */
export function collectExerciseNamesFromExercise(ex) {
  if (!ex) return [];
  const names = [];
  const push = (v) => {
    if (v == null) return;
    const s = String(v);
    if (s.trim() !== "") names.push(s);
  };

  const t = String(ex.exerciseType || ex.type || "single").toLowerCase();
  const hasCircuitData =
    Array.isArray(ex.circuit) &&
    ex.circuit.some((r) => Array.isArray(r) && r.length > 0);
  const hasSubExercises =
    Array.isArray(ex.subExercises) && ex.subExercises.length > 0;

  if (t === "circuit" || hasCircuitData || hasSubExercises) {
    if (Array.isArray(ex.circuit)) {
      for (const round of ex.circuit) {
        if (!Array.isArray(round)) continue;
        for (const sub of round) {
          push(sub?.name);
        }
      }
    }
    if (Array.isArray(ex.subExercises)) {
      for (const sub of ex.subExercises) {
        push(sub?.name);
      }
    }
    push(ex.name);
  } else {
    push(ex.name);
  }
  return names;
}

export function collectExerciseNamesFromFormExercises(exercises) {
  if (!Array.isArray(exercises)) return [];
  const names = [];
  for (const ex of exercises) {
    if (!ex) continue;
    names.push(...collectExerciseNamesFromExercise(ex));
  }
  return names;
}

export function exerciseNameSetFromStrings(names) {
  const set = new Set();
  if (!Array.isArray(names)) return set;
  for (const n of names) {
    if (n == null) continue;
    const s = String(n);
    if (s.trim() !== "") set.add(s);
  }
  return set;
}

/** Highlight when typing matches a previous-week name (Program Creator / AddExercise). */
export function typedMatchesAnyExerciseName(typed, previousNames) {
  if (!previousNames || previousNames.size === 0) return false;
  for (const p of previousNames) {
    if (draftNameMatchesStoredName(typed, p)) return true;
  }
  return false;
}

export function exerciseMatchesDraft(exercise, draftNames) {
  if (!draftNames || draftNames.size === 0) return false;
  const leftNames = collectExerciseNamesFromExercise(exercise);
  for (const left of leftNames) {
    for (const draft of draftNames) {
      if (draftNameMatchesStoredName(draft, left)) return true;
    }
  }
  return false;
}

/** Distinct movement names across workouts (e.g. previous week for AddExercise / Program Creator). */
export function exerciseNameSetFromWorkouts(workouts) {
  const set = new Set();
  for (const w of workouts || []) {
    for (const ex of w.exercises || []) {
      for (const n of collectExerciseNamesFromExercise(ex)) {
        set.add(n);
      }
    }
  }
  return set;
}
